import React, { useState } from 'react';
import {
  App,
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Result,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  useCreatePreparationMutation,
  useDeletePreparationMutation,
  useGetPreparationsQuery,
  useUpdatePreparationMutation,
} from '../../services/recipesApi';
import { useGetInventoryItemsQuery } from '../../services/inventoryApi';
import type { CreateRecipeItemRequest, Preparation } from '../../types/inventory';
import { useResponsive } from '../../hooks/useResponsive';
import { formatCurrencyLocalized, formatUnitCost } from '../../utils/currencyHelpers';
import { getErrorMessage } from '../../utils/errorHelpers';
import { convertToStockUnit } from '../../utils/unitHelpers';
import { RecipeIngredientsEditor, UNIT_OPTIONS } from './RecipeIngredientsEditor';

const { Text } = Typography;

const NUMERIC: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };

// Escala de la cantidad de una preparación y de su rendimiento en el backend
const PREPARATION_MIN_QUANTITY = 0.000001;

// =====================================================
// Form modal
// =====================================================

interface PreparationFormModalProps {
  preparation: Preparation | null;
  onClose: () => void;
  isMobile: boolean;
}

const PreparationFormModal: React.FC<PreparationFormModalProps> = ({
  preparation,
  onClose,
  isMobile,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [createPreparation, { isLoading: creating }] = useCreatePreparationMutation();
  const [updatePreparation, { isLoading: updating }] = useUpdatePreparationMutation();
  const { data: inventoryItems } = useGetInventoryItemsQuery();

  const watchedItems = Form.useWatch('items', form) as
    | Array<{ inventoryItemId?: string; quantity?: number; unit?: string } | undefined>
    | undefined;
  const watchedYield = Form.useWatch('yield', form) as number | undefined;
  const watchedUnit = Form.useWatch('unit', form) as string | undefined;

  const isEditing = !!preparation;

  // Costo en vivo: es para lo que se captura todo esto. Sale del costo que ya tiene cada
  // insumo, así que un insumo sin costo capturado suma cero.
  const batchCost = (watchedItems ?? []).reduce((sum, row) => {
    const item = inventoryItems?.find((i) => i.id === row?.inventoryItemId);
    if (!item || !row?.quantity || !row.unit) return sum;
    return sum + convertToStockUnit(row.quantity, row.unit, item.unit) * item.costPerUnit;
  }, 0);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const items: CreateRecipeItemRequest[] = (values.items || []).map(
        (item: { inventoryItemId: string; quantity: number; unit: string }) => ({
          inventoryItemId: item.inventoryItemId,
          quantity: item.quantity,
          unit: item.unit,
        })
      );

      if (items.length === 0) {
        message.warning('Agrega al menos un ingrediente');
        return;
      }

      const data = { name: values.name, unit: values.unit, yield: values.yield, items };

      if (preparation) {
        await updatePreparation({ id: preparation.id, data }).unwrap();
        message.success('Preparación actualizada');
      } else {
        await createPreparation(data).unwrap();
        message.success('Preparación creada');
      }

      onClose();
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) return;
      message.error(getErrorMessage(error));
    }
  };

  return (
    <Modal
      title={isEditing ? 'Editar Preparación' : 'Nueva Preparación'}
      open
      onCancel={onClose}
      onOk={handleSubmit}
      okText={isEditing ? 'Guardar Cambios' : 'Crear Preparación'}
      cancelText="Cancelar"
      confirmLoading={creating || updating}
      width={isMobile ? '95vw' : 750}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={
          preparation
            ? {
                name: preparation.name,
                unit: preparation.unit,
                yield: preparation.yield,
                items: preparation.items.map((item) => ({
                  inventoryItemId: item.inventoryItemId,
                  quantity: item.quantity,
                  unit: item.unit,
                })),
              }
            : { items: [{ inventoryItemId: undefined, quantity: undefined, unit: undefined }] }
        }
      >
        <Form.Item
          name="name"
          label="Nombre"
          rules={[
            { required: true, whitespace: true, message: 'El nombre es requerido' },
            { max: 200, message: 'Máximo 200 caracteres' },
          ]}
        >
          <Input placeholder="Ej. Salsa roja" />
        </Form.Item>

        <div style={{ display: 'flex', gap: 8 }}>
          <Form.Item
            name="yield"
            label="¿Cuánto rinde?"
            rules={[{ required: true, message: 'Requerido' }]}
            style={{ flex: 1 }}
          >
            <InputNumber min={PREPARATION_MIN_QUANTITY} placeholder="Ej. 5" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="unit"
            label="Unidad"
            rules={[{ required: true, message: 'Requerido' }]}
            style={{ flex: 1 }}
            extra={isEditing ? 'No se puede cambiar: las recetas que la usan dependen de ella' : undefined}
          >
            <Select placeholder="Ej. Litros" options={UNIT_OPTIONS} disabled={isEditing} />
          </Form.Item>
        </div>

        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Captura los ingredientes de todo lo que rinde: si 2 kg de jitomate te dan 5 L de salsa,
          pon 5 L aquí y 2 kg abajo.
        </Text>

        <RecipeIngredientsEditor
          form={form}
          isMobile={isMobile}
          excludeItemId={preparation?.id}
          minQuantity={PREPARATION_MIN_QUANTITY}
        />
      </Form>

      {batchCost > 0 && (
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Text strong style={NUMERIC}>
            Costo del lote: {formatCurrencyLocalized(batchCost)}
          </Text>
          {!!watchedYield && !!watchedUnit && (
            <Text type="secondary" style={{ ...NUMERIC, marginLeft: 12 }}>
              {formatUnitCost(batchCost / watchedYield)} por {watchedUnit}
            </Text>
          )}
        </div>
      )}
    </Modal>
  );
};

// =====================================================
// Main tab
// =====================================================

const PreparationsTab: React.FC = () => {
  const { isMobile } = useResponsive();
  const { message } = App.useApp();

  const { data: preparations = [], isLoading, isError, refetch } = useGetPreparationsQuery();
  const [deletePreparation] = useDeletePreparationMutation();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Preparation | null>(null);

  const handleCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleEdit = (preparation: Preparation) => {
    setEditing(preparation);
    setFormOpen(true);
  };

  const handleClose = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const handleDelete = async (preparation: Preparation) => {
    try {
      await deletePreparation(preparation.id).unwrap();
      message.success('Preparación eliminada');
    } catch (error) {
      message.error(getErrorMessage(error));
    }
  };

  const renderUsage = (preparation: Preparation) =>
    preparation.usedIn.length === 0 ? (
      <Tooltip title="Agrégala como ingrediente de una receta o de un modificador. Mientras no se use, ninguna venta la descuenta.">
        <Tag color="orange">Sin usar</Tag>
      </Tooltip>
    ) : (
      <Tooltip title={preparation.usedIn.join(', ')}>
        <Tag color="green">
          {preparation.usedIn.length} {preparation.usedIn.length === 1 ? 'receta' : 'recetas'}
        </Tag>
      </Tooltip>
    );

  const renderDelete = (preparation: Preparation, button: React.ReactNode) => (
    <Popconfirm
      title="Eliminar preparación"
      description={`¿Eliminar ${preparation.name}?`}
      onConfirm={() => handleDelete(preparation)}
      okText="Eliminar"
      cancelText="Cancelar"
      okButtonProps={{ danger: true }}
    >
      {button}
    </Popconfirm>
  );

  const columns: ColumnsType<Preparation> = [
    { title: 'Preparación', dataIndex: 'name', key: 'name', ellipsis: true },
    {
      title: 'Rinde',
      key: 'yield',
      width: 110,
      align: 'right',
      render: (_, record) => (
        <span style={NUMERIC}>
          {record.yield} {record.unit}
        </span>
      ),
    },
    {
      title: '# Ingredientes',
      key: 'ingredients',
      width: 130,
      align: 'center',
      render: (_, record) => record.items.length,
    },
    {
      title: 'Costo del lote',
      dataIndex: 'batchCost',
      key: 'batchCost',
      width: 140,
      align: 'right',
      render: (value: number) => <span style={NUMERIC}>{formatCurrencyLocalized(value)}</span>,
    },
    {
      title: 'Costo por unidad',
      key: 'costPerUnit',
      width: 150,
      align: 'right',
      render: (_, record) => (
        <span style={NUMERIC}>
          {formatUnitCost(record.costPerUnit)} / {record.unit}
        </span>
      ),
    },
    {
      title: 'Se usa en',
      key: 'usedIn',
      width: 110,
      align: 'center',
      render: (_, record) => renderUsage(record),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            aria-label={`Editar ${record.name}`}
            onClick={() => handleEdit(record)}
          />
          {renderDelete(
            record,
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Eliminar ${record.name}`}
            />
          )}
        </Space>
      ),
    },
  ];

  const renderMobileCard = (preparation: Preparation) => (
    <Card
      key={preparation.id}
      size="small"
      style={{ marginBottom: 8 }}
      actions={[
        <Button
          key="edit"
          type="text"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleEdit(preparation)}
        >
          Editar
        </Button>,
        renderDelete(
          preparation,
          <Button key="delete" type="text" size="small" danger icon={<DeleteOutlined />}>
            Eliminar
          </Button>
        ),
      ]}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Text strong>{preparation.name}</Text>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            Rinde {preparation.yield} {preparation.unit} · {preparation.items.length}{' '}
            {preparation.items.length === 1 ? 'ingrediente' : 'ingredientes'}
          </div>
          <div style={{ marginTop: 4 }}>{renderUsage(preparation)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Text strong style={NUMERIC}>
            {formatCurrencyLocalized(preparation.batchCost)}
          </Text>
          <div style={{ ...NUMERIC, fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            {formatUnitCost(preparation.costPerUnit)} / {preparation.unit}
          </div>
        </div>
      </div>
    </Card>
  );

  const emptyState = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="Todavía no tienes preparaciones. Crea tu primera salsa, caldo o base."
    />
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isMobile ? 12 : 16,
        }}
      >
        <div>
          <Typography.Title level={isMobile ? 5 : 4} style={{ marginBottom: 0 }}>
            Preparaciones
          </Typography.Title>
          {!isMobile && (
            <Text type="secondary">
              Salsas, caldos y bases que no vendes en el menú pero usas en tus recetas. Al vender
              un platillo se descuentan sus ingredientes.
            </Text>
          )}
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} size={isMobile ? 'middle' : 'large'}>
          {isMobile ? 'Nueva' : 'Nueva Preparación'}
        </Button>
      </div>

      {/* Un error no es una lista vacía: invitar a "crear la primera" duplicaría las que ya hay */}
      {isError ? (
        <Result
          status="warning"
          title="No se pudieron cargar tus preparaciones"
          subTitle="Tus preparaciones siguen guardadas. Intenta de nuevo en un momento."
          extra={<Button onClick={() => refetch()}>Reintentar</Button>}
        />
      ) : isMobile ? (
        isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : preparations.length === 0 ? (
          emptyState
        ) : (
          preparations.map(renderMobileCard)
        )
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={preparations}
          loading={isLoading}
          size="middle"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 800 }}
          locale={{ emptyText: emptyState }}
        />
      )}

      {/* Se monta y desmonta completo: una sola instancia reaparecería con la fila anterior */}
      {formOpen && (
        <PreparationFormModal preparation={editing} onClose={handleClose} isMobile={isMobile} />
      )}
    </div>
  );
};

export default PreparationsTab;
